import { useEffect, useRef } from 'react';
import type { StudioState, StudioAction } from '../state/studio';
import { TRACK_COLORS } from '../state/studio';
import { computePeaks } from '../audio/peaks';
import { clamp } from '../format';

export const RULER_H = 26;
export const LANE_H = 74;
const EDGE = 9; // zone de rognage aux bords d'un clip (px)

interface Props {
  s: StudioState;
  dispatch: React.Dispatch<StudioAction>;
  playhead: number;      // position (s) — rendu React (≈10 Hz en lecture)
  livePos?: React.MutableRefObject<number>; // position 60 Hz pour le canvas
  onSeek: (t: number) => void;
}

type DragKind =
  | { kind: 'pan'; x0: number; scroll0: number; y0: number; scEl: HTMLElement | null; top0: number }
  | { kind: 'move'; id: string; grabDt: number; ti0: number }
  | { kind: 'trim'; id: string; side: 'start' | 'end'; x0: number; orig: { start: number; duration: number } }
  | { kind: 'seek' };

interface PeaksCacheEntry {
  buf: AudioBuffer;
  key: string;
  peaks: ReturnType<typeof computePeaks>;
}

export default function Timeline({ s, dispatch, playhead, livePos, onSeek }: Props) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const wrap = useRef<HTMLDivElement | null>(null);
  const drag = useRef<DragKind | null>(null);
  const pointers = useRef<Map<number, number>>(new Map());
  const pinch = useRef<{ t1: number; t2: number } | null>(null);
  const peaksCache = useRef<Map<string, PeaksCacheEntry>>(new Map());
  const live = useRef({ s, playhead, livePos });
  live.current = { s, playhead, livePos };

  const height = RULER_H + s.tracks.length * LANE_H;

  const xToTime = (clientX: number): number => {
    const el = wrap.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return live.current.s.scroll + (clientX - r.left) / live.current.s.pxPerSec;
  };
  const laneAt = (clientY: number): number => {
    const el = wrap.current;
    if (!el) return -1;
    const r = el.getBoundingClientRect();
    const y = clientY - r.top - RULER_H;
    if (y < 0) return -1;
    return Math.min(live.current.s.tracks.length - 1, Math.floor(y / LANE_H));
  };

  /* ------------------------------------------------ dessin */
  useEffect(() => {
    let raf = 0;
    const draw = () => {
      const cv = canvas.current;
      const el = wrap.current;
      if (cv && el) {
        const dpr = window.devicePixelRatio || 1;
        const W = Math.max(1, Math.round(el.clientWidth * dpr));
        const H = Math.max(1, Math.round(height * dpr));
        if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
        const g = cv.getContext('2d');
        if (g) {
          g.setTransform(dpr, 0, 0, dpr, 0, 0);
          const w = el.clientWidth;
          const { s: st, playhead, livePos: lp } = live.current;
          const ph = lp ? lp.current : playhead;
          const pps = st.pxPerSec;
          const scroll = st.scroll;
          const tx = (t: number) => (t - scroll) * pps;

          g.clearRect(0, 0, w, height);
          // fond
          g.fillStyle = '#0e1015';
          g.fillRect(0, 0, w, height);

          // purge du cache de peaks : les clips supprimés ne doivent pas
          // retenir leur AudioBuffer en mémoire
          const cacheRef = peaksCache.current;
          let totalClips = 0;
          for (const t of st.tracks) totalClips += t.clips.length;
          if (cacheRef.size > totalClips + 16) {
            const alive = new Set<string>();
            for (const t of st.tracks) for (const c of t.clips) alive.add(c.id);
            for (const id of cacheRef.keys()) if (!alive.has(id)) cacheRef.delete(id);
          }

          // lignes de mesure / temps
          const secPerBeat = 60 / st.bpm;
          const barSec = secPerBeat * st.beatsPerBar;
          const firstBar = Math.floor(scroll / barSec);
          g.font = '10px system-ui, sans-serif';
          g.textBaseline = 'top';
          for (let bar = firstBar; ; bar++) {
            const t = bar * barSec;
            const x = tx(t);
            if (x > w) break;
            if (x >= -1) {
              g.strokeStyle = 'rgba(121,134,203,0.5)';
              g.lineWidth = 1;
              g.beginPath(); g.moveTo(Math.round(x) + 0.5, 0); g.lineTo(Math.round(x) + 0.5, height); g.stroke();
              g.fillStyle = 'rgba(180,186,210,0.75)';
              g.fillText(String(bar + 1), x + 4, 4);
            }
            // temps intermédiaires
            for (let bt = 1; bt < st.beatsPerBar; bt++) {
              const xb = tx(t + bt * secPerBeat);
              if (xb < 0 || xb > w) continue;
              g.strokeStyle = 'rgba(121,134,203,0.18)';
              g.beginPath(); g.moveTo(Math.round(xb) + 0.5, RULER_H); g.lineTo(Math.round(xb) + 0.5, height); g.stroke();
            }
          }

          // lanes + clips
          st.tracks.forEach((track, i) => {
            const y = RULER_H + i * LANE_H;
            g.strokeStyle = 'rgba(255,255,255,0.06)';
            g.beginPath(); g.moveTo(0, y + LANE_H + 0.5); g.lineTo(w, y + LANE_H + 0.5); g.stroke();

            const color = TRACK_COLORS[i % TRACK_COLORS.length];
            for (const c of track.clips) {
              const x0 = tx(c.start);
              const cw = c.duration * pps;
              if (x0 + cw < 0 || x0 > w) continue;
              const pad = 4;
              const cy = y + pad;
              const ch = LANE_H - pad * 2;
              const sel = c.id === st.selectedClip;
              // corps
              g.fillStyle = hexA(color, sel ? 0.32 : 0.2);
              roundRect(g, x0, cy, Math.max(2, cw), ch, 6);
              g.fill();
              g.strokeStyle = hexA(color, sel ? 1 : 0.7);
              g.lineWidth = sel ? 2 : 1;
              g.stroke();
              // mini forme d'onde
              const innerW = Math.max(1, Math.min(cw, w) - 2);
              if (innerW > 4 && cw > 6) {
                // colonnes quantifiées par puissance de 2 pour ne pas invalider
                // le cache à chaque pixel de zoom
                const cols = Math.min(1024, Math.max(32, 1 << Math.round(Math.log2(Math.floor(innerW)))));
                const key = `${cols}|${c.offset.toFixed(3)}|${c.duration.toFixed(3)}`;
                const cache = peaksCache.current;
                let entry = cache.get(c.id);
                if (!entry || entry.buf !== c.buffer || entry.key !== key) {
                  if (cache.size > 128) cache.clear();
                  entry = { buf: c.buffer, key, peaks: computePeaks(c.buffer, cols, c.offset, c.offset + c.duration) };
                  cache.set(c.id, entry);
                }
                const peaks = entry.peaks;
                const mid = cy + ch / 2;
                g.fillStyle = hexA(color, 0.9);
                const scale = cw / peaks.columns;
                for (let k = 0; k < peaks.columns; k++) {
                  const px = x0 + k * scale;
                  if (px < -scale || px > w) continue;
                  const hi = peaks.max[k] * (ch / 2) * 0.9;
                  const lo = peaks.min[k] * (ch / 2) * 0.9;
                  g.fillRect(px, mid - hi, Math.max(1, scale), Math.max(1, hi - lo));
                }
              }
              // label
              g.fillStyle = 'rgba(255,255,255,0.85)';
              g.font = '11px system-ui, sans-serif';
              g.textBaseline = 'top';
              const label = c.name.length > 22 ? c.name.slice(0, 21) + '…' : c.name;
              g.save();
              g.beginPath(); g.rect(x0, cy, Math.max(2, cw), ch); g.clip();
              g.fillText(label, x0 + 6, cy + 4);
              g.restore();
            }
          });

          // tête de lecture
          const hx = tx(ph);
          if (hx >= 0 && hx <= w) {
            g.strokeStyle = '#ffffff';
            g.lineWidth = 2;
            g.beginPath(); g.moveTo(hx, 0); g.lineTo(hx, height); g.stroke();
            g.fillStyle = '#ffffff';
            g.beginPath(); g.moveTo(hx - 5, 0); g.lineTo(hx + 5, 0); g.lineTo(hx, 7); g.closePath(); g.fill();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [height]);

  /* ------------------------------------------------ molette (zoom / scroll) */
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const st = live.current.s;
      if (e.ctrlKey || Math.abs(e.deltaX) < Math.abs(e.deltaY)) {
        // zoom centré sous le curseur
        const t = xToTime(e.clientX);
        const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
        const pps = clamp(st.pxPerSec * factor, 8, 600);
        const r = el.getBoundingClientRect();
        const scroll = Math.max(0, t - (e.clientX - r.left) / pps);
        dispatch({ type: 'view', pxPerSec: pps, scroll });
      } else {
        dispatch({ type: 'view', scroll: Math.max(0, st.scroll + e.deltaX / st.pxPerSec) });
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [dispatch]);

  /* ------------------------------------------------ pointeur */
  const hitClip = (clientX: number, clientY: number) => {
    const st = live.current.s;
    const ti = laneAt(clientY);
    if (ti < 0 || ti >= st.tracks.length) return null;
    const t = xToTime(clientX);
    const track = st.tracks[ti];
    // parcours inverse : le dernier clip dessiné est visuellement au-dessus
    for (let k = track.clips.length - 1; k >= 0; k--) {
      const c = track.clips[k];
      if (t >= c.start && t <= c.start + c.duration) {
        const x0 = (c.start - st.scroll) * st.pxPerSec;
        const cw = c.duration * st.pxPerSec;
        const px = (clientX - (wrap.current?.getBoundingClientRect().left ?? 0));
        let side: 'start' | 'end' | null = null;
        // un clip trop étroit n'a pas de zones de rognage (sinon impossible à déplacer)
        if (cw >= EDGE * 3) {
          if (px - x0 < EDGE) side = 'start';
          else if (x0 + cw - px < EDGE) side = 'end';
        }
        return { track, clip: c, ti, side };
      }
    }
    return { track, clip: null, ti, side: null };
  };

  const onDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, e.clientX);
    if (pointers.current.size === 2) {
      drag.current = null;
      const st = live.current.s;
      const xs = [...pointers.current.values()];
      const r = wrap.current!.getBoundingClientRect();
      const f = (x: number) => st.scroll + (x - r.left) / st.pxPerSec;
      pinch.current = { t1: f(Math.min(xs[0], xs[1])), t2: f(Math.max(xs[0], xs[1])) };
      return;
    }
    if (pointers.current.size > 2 || pinch.current) return; // 3e doigt : on ignore
    const r = wrap.current!.getBoundingClientRect();
    if (e.clientY - r.top < RULER_H) { drag.current = { kind: 'seek' }; onSeek(Math.max(0, xToTime(e.clientX))); dispatch({ type: 'playhead', pos: Math.max(0, xToTime(e.clientX)) }); return; }
    const hit = hitClip(e.clientX, e.clientY);
    if (hit && hit.clip) {
      dispatch({ type: 'clip/select', id: hit.clip.id });
      if (hit.side) drag.current = {
        kind: 'trim', id: hit.clip.id, side: hit.side, x0: e.clientX,
        orig: { start: hit.clip.start, duration: hit.clip.duration },
      };
      else drag.current = { kind: 'move', id: hit.clip.id, grabDt: xToTime(e.clientX) - hit.clip.start, ti0: hit.ti };
    } else {
      // zone vide : pan horizontal + défilement vertical du conteneur
      const scEl = (wrap.current?.closest('.studio-body') ?? null) as HTMLElement | null;
      drag.current = {
        kind: 'pan', x0: e.clientX, scroll0: live.current.s.scroll,
        y0: e.clientY, scEl, top0: scEl?.scrollTop ?? 0,
      };
    }
  };

  const onMove = (e: React.PointerEvent) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, e.clientX);
    if (pinch.current && pointers.current.size === 2) {
      const xs = [...pointers.current.values()].sort((a, b) => a - b);
      const r = wrap.current!.getBoundingClientRect();
      const f1 = (xs[0] - r.left) / r.width, f2 = (xs[1] - r.left) / r.width;
      const dt = pinch.current.t2 - pinch.current.t1;
      if (f2 - f1 > 0.02 && dt > 0.001) { // doigts alignés verticalement : dt≈0, on ne zoome pas
        const spanPx = r.width * (f2 - f1);
        const pps = clamp(spanPx / dt, 8, 600);
        const scroll = Math.max(0, pinch.current.t1 - (xs[0] - r.left) / pps);
        dispatch({ type: 'view', pxPerSec: pps, scroll });
      }
      return;
    }
    if (pointers.current.size > 2) return; // 3 doigts et plus : on attend
    const d = drag.current;
    if (!d) return;
    const st = live.current.s;
    if (d.kind === 'pan') {
      dispatch({ type: 'view', scroll: Math.max(0, d.scroll0 - (e.clientX - d.x0) / st.pxPerSec) });
      if (d.scEl) d.scEl.scrollTop = d.top0 - (e.clientY - d.y0);
    } else if (d.kind === 'seek') {
      const t = Math.max(0, xToTime(e.clientX));
      onSeek(t); dispatch({ type: 'playhead', pos: t });
    } else if (d.kind === 'move') {
      const start = Math.max(0, xToTime(e.clientX) - d.grabDt);
      const ti = laneAt(e.clientY);
      // la piste d'origine peut avoir disparu pendant le drag
      const toTrack = (ti >= 0 && ti < st.tracks.length ? st.tracks[ti] : undefined)
        ?? st.tracks[d.ti0] ?? st.tracks[0];
      if (!toTrack) return;
      dispatch({ type: 'clip/move', id: d.id, toTrackId: toTrack.id, start: snap(start, st) });
    } else if (d.kind === 'trim') {
      // delta ABSOLU depuis le début du drag : si le reducer a borné un
      // mouvement, le bord recolle au curseur au lieu de rester décalé
      const dt = (e.clientX - d.x0) / st.pxPerSec;
      let cur: { start: number; duration: number } | null = null;
      for (const tr of st.tracks) {
        const c = tr.clips.find((x) => x.id === d.id);
        if (c) { cur = c; break; }
      }
      if (!cur) return;
      const delta = d.side === 'start'
        ? d.orig.start + dt - cur.start
        : d.orig.duration + dt - cur.duration;
      if (Math.abs(delta) > 0.001) dispatch({ type: 'clip/trim', id: d.id, side: d.side, delta });
    }
  };

  const onUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 2) {
      // 3 doigts → 2 : on RÉ-ANCRE le pinch sur la paire restante
      // (sinon les anciens ancrages provoquent un saut de zoom)
      const st = live.current.s;
      const xs = [...pointers.current.values()];
      const r = wrap.current?.getBoundingClientRect();
      if (r) {
        const f = (x: number) => st.scroll + (x - r.left) / st.pxPerSec;
        pinch.current = { t1: f(Math.min(xs[0], xs[1])), t2: f(Math.max(xs[0], xs[1])) };
      }
    } else if (pointers.current.size < 2) {
      pinch.current = null;
    }
    drag.current = null;
  };

  return (
    <div className="tl-wrap" ref={wrap} style={{ height }}>
      <canvas ref={canvas} className="tl-canvas" />
      <div
        className="tl-hit"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      />
    </div>
  );
}

/* utilitaires dessin */
function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  g.beginPath();
  g.moveTo(x + rr, y);
  g.arcTo(x + w, y, x + w, y + h, rr);
  g.arcTo(x + w, y + h, x, y + h, rr);
  g.arcTo(x, y + h, x, y, rr);
  g.arcTo(x, y, x + w, y, rr);
  g.closePath();
}
function hexA(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
function snap(t: number, st: StudioState): number {
  // aimant sur le battement, calibré en PIXELS (8 px) pour rester léger à
  // tous les niveaux de zoom ; coupé quand les battements sont trop denses
  const beat = 60 / st.bpm;
  const beatPx = beat * st.pxPerSec;
  if (beatPx < 16) return t;
  const near = Math.round(t / beat) * beat;
  return Math.abs(near - t) * st.pxPerSec < 8 ? near : t;
}
